import React, { useRef, useState } from 'react';
import {
  Box, Typography, TextField, Chip, Alert, Paper,
  Tabs, Tab, Button, LinearProgress,
} from '@mui/material';
import { YouTube, VideoLibrary, UploadFile, CloudUpload } from '@mui/icons-material';
import { contentAPI } from '../../services/api';

interface Props {
  url: string;
  description: string;
  onChange: (data: { url: string; provider: string; description: string }) => void;
}

function detectProvider(url: string): string {
  if (url.includes('youtube.com') || url.includes('youtu.be')) return 'youtube';
  if (url.includes('vimeo.com')) return 'vimeo';
  if (url.startsWith('/uploads/')) return 'upload';
  return 'url';
}

function toEmbedUrl(url: string): string {
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  if (ytMatch) return `https://www.youtube.com/embed/${ytMatch[1]}`;
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
  if (vimeoMatch) return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
  return url;
}

function isUploadedVideo(url: string): boolean {
  return url.startsWith('/uploads/') || url.includes('/uploads/') || /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);
}

const API_ORIGIN = (process.env.REACT_APP_API_URL || 'http://localhost:8080/api').replace(/\/api$/, '');

function toAbsoluteUrl(url: string): string {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${API_ORIGIN}${url}`;
}

const VideoContentEditor: React.FC<Props> = ({ url, description, onChange }) => {
  const initialTab = isUploadedVideo(url) ? 1 : 0;
  const [tab, setTab] = useState(initialTab);
  const [inputUrl, setInputUrl] = useState(isUploadedVideo(url) ? '' : url);
  const [embedUrl, setEmbedUrl] = useState(isUploadedVideo(url) ? '' : toEmbedUrl(url));
  const [uploadedUrl, setUploadedUrl] = useState(isUploadedVideo(url) ? url : '');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const provider = detectProvider(inputUrl);

  const handleUrlChange = (val: string) => {
    setInputUrl(val);
    const embed = toEmbedUrl(val);
    setEmbedUrl(embed);
    onChange({ url: embed, provider: detectProvider(val), description });
  };

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('video/')) {
      setUploadError('Please select a valid video file (MP4, WebM, etc.)');
      return;
    }
    setUploadError(null);
    setUploading(true);
    setUploadProgress(0);
    try {
      const res = await contentAPI.uploadFile(file, pct => setUploadProgress(pct));
      const fileUrl = res.data.url;
      setUploadedUrl(fileUrl);
      onChange({ url: fileUrl, provider: 'upload', description });
    } catch {
      setUploadError('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  return (
    <Box>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Embed URL" icon={<YouTube fontSize="small" />} iconPosition="start" />
        <Tab label="Upload MP4" icon={<UploadFile fontSize="small" />} iconPosition="start" />
      </Tabs>

      {tab === 0 && (
        <Box>
          <TextField
            fullWidth
            label="Video URL"
            placeholder="https://www.youtube.com/watch?v=... or https://vimeo.com/..."
            value={inputUrl}
            onChange={e => handleUrlChange(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              endAdornment: provider !== 'url' && provider !== 'upload' ? (
                <Chip
                  icon={provider === 'youtube' ? <YouTube /> : <VideoLibrary />}
                  label={provider}
                  size="small"
                  color={provider === 'youtube' ? 'error' : 'primary'}
                  sx={{ mr: 1 }}
                />
              ) : null,
            }}
          />
          <Alert severity="info" sx={{ mb: 2 }}>
            Supports YouTube, Vimeo, or any direct embed URL. Paste a YouTube watch URL — it will be auto-converted.
          </Alert>
          <TextField
            fullWidth
            label="Video Description"
            multiline
            rows={2}
            value={description}
            onChange={e => onChange({ url: embedUrl, provider, description: e.target.value })}
            sx={{ mb: 2 }}
            placeholder="Brief description of what this video covers..."
          />
          {embedUrl && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Preview</Typography>
              <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1 }}>
                <Box sx={{ position: 'relative', paddingTop: '56.25%' }}>
                  <iframe
                    src={embedUrl}
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title="Video preview"
                  />
                </Box>
              </Paper>
            </Box>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {/* Drop zone */}
          <Paper
            variant="outlined"
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={handleDrop}
            sx={{
              p: 5, mb: 2, textAlign: 'center', cursor: 'pointer',
              borderStyle: 'dashed', borderWidth: 2,
              bgcolor: dragOver ? 'action.hover' : 'background.default',
              borderColor: dragOver ? 'primary.main' : 'divider',
              transition: 'all 0.2s',
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={600}>
              Drag & drop an MP4 file here
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              or click to browse — MP4, WebM, MOV supported (up to 500 MB)
            </Typography>
            <Button variant="outlined" size="small" startIcon={<UploadFile />}>
              Choose File
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              style={{ display: 'none' }}
              onChange={e => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
            />
          </Paper>

          {uploading && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Uploading… {uploadProgress}%
              </Typography>
              <LinearProgress variant="determinate" value={uploadProgress} />
            </Box>
          )}

          {uploadError && <Alert severity="error" sx={{ mb: 2 }}>{uploadError}</Alert>}

          <TextField
            fullWidth
            label="Video Description"
            multiline
            rows={2}
            value={description}
            onChange={e => onChange({ url: uploadedUrl, provider: 'upload', description: e.target.value })}
            sx={{ mb: 2 }}
            placeholder="Brief description of what this video covers..."
          />

          {uploadedUrl && (
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Preview</Typography>
              <Paper variant="outlined" sx={{ overflow: 'hidden', borderRadius: 1, p: 1 }}>
                <video
                  src={toAbsoluteUrl(uploadedUrl)}
                  controls
                  style={{ width: '100%', borderRadius: 4 }}
                />
              </Paper>
              <Alert severity="success" sx={{ mt: 1 }}>
                Video uploaded successfully.
              </Alert>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default VideoContentEditor;
